/**
 * WorkspaceSwitcher.jsx — ELYON-SHELL-FOUNDATION-1
 *
 * Workspace/tenant selector in the sidebar header.
 * Shows current tenant brand name + dropdown for settings / sign-out.
 * SIDEBAR-IMPORT-GATE: Building2, ChevronDown, LogOut, Settings from lucide-react.
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Building2, ChevronDown, LogOut, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTenantBrand, useTenantId } from '../stores/tenantStore.js';
import { auth } from '../firebase/config.js';

/**
 * @param {{ isCollapsed: boolean }} props
 */
export function WorkspaceSwitcher({ isCollapsed }) {
  const brand    = useTenantBrand();
  const tenantId = useTenantId();
  const navigate = useNavigate();

  async function handleSignOut() {
    try {
      if (auth) {
        const { signOut } = await import('firebase/auth');
        await signOut(auth);
      }
    } catch (err) {
      console.error('Sign-out failed:', err);
    }
  }

  const displayName = brand?.name ?? 'Elyon Platform';

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="elyon-workspace-trigger"
          aria-label={isCollapsed ? `${displayName} — workspace menu` : undefined}
          aria-haspopup="menu"
          style={isCollapsed ? { justifyContent: 'center' } : undefined}
        >
          <span className="elyon-workspace-icon" aria-hidden="true">
            {brand?.logoUrl ? (
              <img
                src={brand.logoUrl}
                alt=""
                className="elyon-workspace-logo"
              />
            ) : (
              <Building2 size={14} strokeWidth={1.5} />
            )}
          </span>

          {!isCollapsed && (
            <>
              <span className="elyon-workspace-name">{displayName}</span>
              <ChevronDown
                size={14}
                strokeWidth={1.5}
                className="elyon-workspace-chevron"
                aria-hidden="true"
              />
            </>
          )}
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="elyon-dropdown-content"
          align="start"
          sideOffset={4}
        >
          <DropdownMenu.Label className="elyon-dropdown-label">
            Workspace
          </DropdownMenu.Label>

          <DropdownMenu.Item asChild>
            <div className="elyon-dropdown-item" style={{ cursor: 'default' }}>
              <Building2 size={14} strokeWidth={1.5} aria-hidden="true" />
              <span>{displayName}</span>
              <span
                style={{ marginLeft: 'auto', color: 'var(--color-brand-primary)' }}
                aria-label="Current workspace"
              >
                ✓
              </span>
            </div>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="elyon-dropdown-separator" />

          <DropdownMenu.Item
            className="elyon-dropdown-item"
            onSelect={() => navigate('/settings')}
          >
            <Settings size={14} strokeWidth={1.5} aria-hidden="true" />
            <span>Workspace settings</span>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="elyon-dropdown-separator" />

          <DropdownMenu.Item
            className="elyon-dropdown-item destructive"
            onSelect={handleSignOut}
          >
            <LogOut size={14} strokeWidth={1.5} aria-hidden="true" />
            <span>Sign out</span>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
